import {
  CTA,
  PRACTICES,
  PRACTICES_SECTION,
  STATUS_LABEL,
  notifyMeHref,
  sectionLabel,
} from './homepageContent';

/**
 * "Three practices. One chamber." — the roadmap, with availability explicit: Criminal is the
 * live product (Beta Live, Start Free Trial); Civil and Corporate are coming soon and offer
 * only "Notify me", which writes to support and never routes into the app. Each card is the
 * target of the header's Product menu (#practice-<key>). Placed directly under the film
 * (Ajay, 2026-11-01).
 */

interface PracticesBandProps {
  onLogin: () => void;
  loading: boolean;
}

const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass-ink focus-visible:outline-offset-2';

export default function PracticesBand({ onLogin, loading }: PracticesBandProps) {
  return (
    <section
      id="practices"
      aria-labelledby="practices-heading"
      className="scroll-mt-16 bg-parchment px-5 sm:px-8 lg:px-14 py-16 sm:py-20"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h2
          id="practices-heading"
          className="font-display font-normal text-3xl lg:text-[44px] text-chamber-panel"
        >
          {PRACTICES_SECTION.headingLead}{' '}
          <em className="font-editorial italic text-brass-ink">{PRACTICES_SECTION.headingEmphasis}</em>
        </h2>
        <span className="font-plex text-[11px] tracking-[0.3em] text-brass-deep">
          {sectionLabel('practices')}
        </span>
      </div>

      <div className="grid lg:grid-cols-3 gap-px bg-parchment-border-strong border border-parchment-border-strong mt-10">
        {PRACTICES.map((p, i) => {
          const live = p.status === 'beta_live';
          return (
            <div
              key={p.key}
              id={`practice-${p.key}`}
              className={`scroll-mt-20 p-8 lg:p-9 relative flex flex-col ${
                live ? 'bg-parchment-card' : 'bg-parchment'
              }`}
            >
              <span
                aria-hidden="true"
                className="absolute top-5 right-7 font-editorial text-[64px] leading-none text-brass-ink/[0.22] select-none pointer-events-none"
              >
                {['I', 'II', 'III'][i]}
              </span>
              <span
                className={`self-start font-plex text-[10.5px] font-medium tracking-[0.14em] uppercase px-2 py-1 ${
                  live ? 'bg-chamber-panel text-brass' : 'border border-parchment-border-strong text-parchment-muted'
                }`}
              >
                {STATUS_LABEL[p.status]}
              </span>
              <h3 className="mt-5 font-display font-normal text-[27px] text-chamber-panel">{p.name}</h3>
              <p className="mt-3 flex-1 text-[14.5px] leading-[1.65] text-parchment-muted max-w-[320px]">
                {p.card}
              </p>
              {live ? (
                <button
                  type="button"
                  onClick={onLogin}
                  disabled={loading}
                  className={`mt-7 self-start bg-chamber-panel text-parchment px-6 py-3 text-[12px] font-semibold tracking-[0.1em] uppercase hover:bg-chamber-ink transition-colors disabled:opacity-50 cursor-pointer ${FOCUS}`}
                >
                  {CTA.trial}
                </button>
              ) : (
                <a
                  href={notifyMeHref(p)}
                  className={`mt-7 self-start border border-brass-ink text-brass-deep px-6 py-3 text-[12px] font-semibold tracking-[0.1em] uppercase hover:bg-brass-ink hover:text-parchment transition-colors ${FOCUS}`}
                >
                  {CTA.notify}
                  <span className="sr-only"> when {p.name} is available</span>
                </a>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
